import apiClient from '../../shared/services/apiClient';

/**
 * DNS Service (Admin Version)
 *
 * Handles admin-level DNS operations with full CRUD capabilities.
 * Admins can manage DNS resources for all users.
 *
 * All routes use /api/admin/dns/* prefix
 */
class DNSService {
  // ==========================================
  // Zone Management (Full CRUD)
  // ==========================================

  /**
   * Get all DNS zones (including all users)
   * @param {object} options - Options (includeInactive, sortField, sortOrder, search)
   */
  async getDnsZones(options = {}) {
    try {
      const params = {};
      if (options.include_inactive || options.includeInactive) {
        params.include_inactive = true;
      }
      if (options.sortField) {
        params.sortField = options.sortField;
      }
      if (options.sortOrder) {
        params.sortOrder = options.sortOrder;
      }
      if (options.search) {
        params.search = options.search;
      }
      // 服务端分页：传 page / page_size 时返回 { data, pagination, stats }
      ['page', 'page_size', 'status', 'user_id', 'product_id'].forEach((k) => {
        if (options[k] !== undefined && options[k] !== null && options[k] !== '') params[k] = options[k];
      });
      const response = await apiClient.get('/api/admin/dns/zones', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single DNS zone details
   * @param {number} zoneId - Zone ID
   */
  async getDnsZone(zoneId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/zones/get?id=${zoneId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create DNS zone (admin can create for any user)
   * @param {object} zoneData - Zone data including user_id
   */
  async createDnsZone(zoneData) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/create', zoneData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update DNS zone
   * @param {object} zoneData - Zone data with id
   */
  async updateDnsZone(zoneData) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/update', zoneData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete DNS zone
   * @param {number} zoneId - Zone ID
   */
  async deleteDnsZone(zoneId) {
    try {
      const response = await apiClient.delete('/api/admin/dns/zones/delete', {
        data: { id: zoneId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Zone Lifecycle Management (Admin Only)
  // ==========================================

  /**
   * 标记区域归属已验证
   */
  async markZoneVerified(id) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/mark-verified', { id });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Suspend DNS zone
   * @param {number} id - Zone ID
   * @param {string} reason - Suspension reason
   */
  async suspendZone(id, reason = '') {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/suspend', { id, reason });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Resume DNS zone
   * @param {number} id - Zone ID
   */
  async resumeZone(id) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/resume', { id });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Terminate DNS zone
   * @param {number} id - Zone ID
   * @param {boolean} hardDelete - Whether to permanently delete
   */
  async terminateZone(id, hardDelete = false) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/terminate', { id, hard_delete: hardDelete });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Activate DNS zone
   * @param {number} id - Zone ID
   */
  async activateZone(id) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/activate', { id });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Change zone owner
   * @param {number} id - Zone ID
   * @param {number} newUserId - New user ID
   */
  async changeZoneOwner(id, newUserId) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/change-owner', { id, new_user_id: newUserId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Force sync zone to all nodes
   * @param {number} id - Zone ID
   */
  async forceSyncZone(id) {
    try {
      const response = await apiClient.post('/api/admin/dns/zones/force-sync', { id });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Record Management (Full CRUD)
  // ==========================================

  /**
   * Get DNS records (admin can access any zone's records)
   * @param {object} params - Query parameters
   */
  async getDnsRecords(params) {
    try {
      const response = await apiClient.get('/api/admin/dns/records', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create DNS record
   * @param {object} recordData - Record data
   */
  async createDnsRecord(recordData) {
    try {
      const response = await apiClient.post('/api/admin/dns/records/create', recordData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update DNS record
   * @param {object} recordData - Record data with id
   */
  async updateDnsRecord(recordData) {
    try {
      const response = await apiClient.post('/api/admin/dns/records/update', recordData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete DNS record
   * @param {number} recordId - Record ID
   */
  async deleteDnsRecord(recordId) {
    try {
      const response = await apiClient.delete('/api/admin/dns/records/delete', {
        data: { id: recordId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Bulk import DNS records
   * @param {object} data - Import data with zone_id and records
   */
  async bulkImportRecords(data) {
    try {
      const response = await apiClient.post('/api/admin/dns/records/bulk-import', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // CIDR Management (Full CRUD)
  // ==========================================

  /**
   * Get CIDR groups
   * @param {string} search - Optional search term
   */
  async getCidrs(search = '') {
    try {
      const response = await apiClient.get('/api/admin/dns/cidrs', {
        params: { search }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create CIDR group
   * @param {object} cidrData - CIDR group data
   */
  async createCidr(cidrData) {
    try {
      const response = await apiClient.post('/api/admin/dns/cidrs/create', cidrData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update CIDR group
   * @param {object} cidrData - CIDR group data with id
   */
  async updateCidr(cidrData) {
    try {
      const response = await apiClient.post('/api/admin/dns/cidrs/update', cidrData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete CIDR group
   * @param {number} cidrId - CIDR group ID
   */
  async deleteCidr(cidrId) {
    try {
      const response = await apiClient.delete('/api/admin/dns/cidrs/delete', {
        data: { id: cidrId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get CIDR entries for a group
   * @param {number} cidrId - CIDR group ID
   * @param {string} search - Optional search term
   */
  async getCidrEntries(cidrId, search = '') {
    try {
      const response = await apiClient.get('/api/admin/dns/cidr-entries', {
        params: { cidr_id: cidrId, search }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create CIDR entry
   * @param {object} entryData - CIDR entry data
   */
  async createCidrEntry(entryData) {
    try {
      const response = await apiClient.post('/api/admin/dns/cidr-entries/create', entryData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update CIDR entry
   * @param {object} entryData - CIDR entry data with id
   */
  async updateCidrEntry(entryData) {
    try {
      const response = await apiClient.post('/api/admin/dns/cidr-entries/update', entryData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete CIDR entry
   * @param {number} entryId - CIDR entry ID
   */
  async deleteCidrEntry(entryId) {
    try {
      const response = await apiClient.delete('/api/admin/dns/cidr-entries/delete', {
        data: { id: entryId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Download CIDR configuration
   * @param {number} cidrId - CIDR group ID
   * @param {number} ipVersion - Optional IP version (4 or 6)
   */
  async downloadCidrConfig(cidrId, ipVersion = null) {
    try {
      let url = `/api/admin/dns/cidr-download?cidr_id=${cidrId}`;
      if (ipVersion) {
        url += `&ip_version=${ipVersion}`;
      }
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get CIDR usage for a zone
   * @param {number} zoneId - Zone ID
   */
  async getCidrUsage(zoneId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/cidr_usage?zone_id=${zoneId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // DNS Node Group Management (Admin Only)
  // ==========================================

  async getNodeParentGroups() {
    try {
      const response = await apiClient.get('/api/admin/dns/node-parent-groups');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async saveNodeParentGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/dns/node-parent-groups/save', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getNodeChildGroups(parentGroupId = null) {
    try {
      const query = parentGroupId ? `?parent_group_id=${parentGroupId}` : '';
      const response = await apiClient.get(`/api/admin/dns/node-child-groups${query}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async saveNodeChildGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/dns/node-child-groups/save', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async assignChildGroupNodes(childGroupId, nodeIds) {
    try {
      const response = await apiClient.post('/api/admin/dns/node-child-groups/assign-nodes', {
        child_group_id: childGroupId,
        node_ids: nodeIds
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deleteNodeParentGroup(id) {
    try {
      const response = await apiClient.post('/api/admin/dns/node-parent-groups/delete', { id });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deleteNodeChildGroup(id) {
    try {
      const response = await apiClient.post('/api/admin/dns/node-child-groups/delete', { id });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // DNS Node Management (Admin Only)
  // ==========================================

  /**
   * Get DNS nodes
   */
  async getDnsNodes() {
    try {
      const response = await apiClient.get('/api/admin/dns/nodes');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create DNS node
   * @param {object} nodeData - Node data
   */
  async createDnsNode(nodeData) {
    try {
      const response = await apiClient.post('/api/admin/dns/nodes/add', nodeData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update DNS node
   * @param {object} nodeData - Node data with id
   */
  async updateDnsNode(nodeData) {
    try {
      const response = await apiClient.post('/api/admin/dns/nodes/update', nodeData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete DNS node
   * @param {number} nodeId - Node ID
   */
  async deleteDnsNode(nodeId) {
    try {
      const response = await apiClient.delete('/api/admin/dns/nodes/delete', {
        data: { id: nodeId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Reset node sync key
   * @param {number} nodeId - Node ID
   */
  async resetNodeKey(nodeId) {
    try {
      const response = await apiClient.post('/api/admin/dns/nodes/reset-key', {
        id: nodeId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get node sync status
   * @param {number} nodeId - Node ID
   */
  async getNodeSyncStatus(nodeId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/nodes/sync-status?id=${nodeId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Check node health
   * @param {number} nodeId - Node ID
   */
  async checkNodeHealth(nodeId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/nodes/${nodeId}/health`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Geographic Information (Read-Only, shared with users)
  // ==========================================

  /**
   * Get all continents
   */
  async getContinents() {
    try {
      const response = await apiClient.get('/api/admin/dns/geo/continents');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get countries by continent
   * @param {number} continentId - Continent ID
   */
  async getCountriesByContinent(continentId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/countries?continent_id=${continentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get regions by country
   * @param {string} countryId - Country code
   */
  async getRegions(countryId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/regions?country_code=${countryId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get cities by country and region
   * @param {string} countryId - Country code
   * @param {number} regionId - Region ID
   */
  async getCities(countryId, regionId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/cities?country_code=${countryId}&region_id=${regionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get continent information by IDs
   * @param {array} continentIds - Array of continent IDs
   */
  async getContinentInfo(continentIds) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/info/continents?ids=${continentIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get country information by IDs
   * @param {array} countryIds - Array of country codes
   */
  async getCountryInfo(countryIds) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/info/countries?ids=${countryIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get province information by IDs
   * @param {array} provinceIds - Array of province IDs
   */
  async getProvinceInfo(provinceIds) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/info/provinces?ids=${provinceIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get city information by IDs
   * @param {array} cityIds - Array of city IDs
   */
  async getCityInfo(cityIds) {
    try {
      const response = await apiClient.get(`/api/admin/dns/geo/info/cities?ids=${cityIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // ISP Information (Read-Only, shared with users)
  // ==========================================

  /**
   * Get countries with ISP data
   */
  async getIspCountries() {
    try {
      const response = await apiClient.get('/api/admin/dns/isp/countries');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP types for a country
   * @param {string} country - Country code
   */
  async getIspTypes(country) {
    try {
      const response = await apiClient.get(`/api/admin/dns/isp/types?country=${encodeURIComponent(country)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP providers
   * @param {string} country - Country code
   * @param {string} type - Optional ISP type
   * @param {string} search - Optional search term
   */
  async getIsps(country, type = null, search = null) {
    let url = `/api/admin/dns/isp/providers?country=${encodeURIComponent(country)}`;

    if (type) {
      url += `&usage_type=${encodeURIComponent(type)}`;
    }

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get ISP information by IDs
   * @param {array} ispIds - Array of ISP IDs
   */
  async getIspInfo(ispIds) {
    try {
      const response = await apiClient.get(`/api/admin/dns/isp/info?ids=${ispIds.join(',')}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // QoS Policy Management (Full CRUD)
  // ==========================================

  /**
   * Get QoS policies for a zone
   * @param {number} zoneId - Zone ID
   */
  async getQosPolicies(zoneId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/qos?zone_id=${zoneId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single QoS policy
   * @param {number} policyId - Policy ID
   */
  async getQosPolicy(policyId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/qos?id=${policyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create QoS policy
   * @param {object} policyData - Policy data
   */
  async createQosPolicy(policyData) {
    try {
      const response = await apiClient.post('/api/admin/dns/qos/create', policyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update QoS policy
   * @param {object} policyData - Policy data with id
   */
  async updateQosPolicy(policyData) {
    try {
      const response = await apiClient.post('/api/admin/dns/qos/update', policyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete QoS policy
   * @param {number} policyId - Policy ID
   */
  async deleteQosPolicy(policyId) {
    try {
      const response = await apiClient.delete('/api/admin/dns/qos/delete', {
        data: { id: policyId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Download QoS configuration
   * @param {string} zone - Zone name
   */
  async downloadQosConfig(zone) {
    try {
      const response = await apiClient.get(`/api/admin/dns/qos-download?zone=${zone}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Configuration Sync (Admin Only)
  // ==========================================

  /**
   * Sync configuration to nodes
   * @param {object} data - Sync data (zone_id, node_ids, etc.)
   */
  async syncToNodes(data) {
    try {
      const response = await apiClient.post('/api/admin/dns/sync', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Banlist Management (Admin Only)
  // ==========================================

  /**
   * Get banlist entries
   * @param {object} params - Query parameters (page, pageSize, search, scene, device_type)
   */
  async getBanlist(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/banlist?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get banlist statistics
   */
  async getBanlistStats() {
    try {
      const response = await apiClient.get('/api/admin/dns/banlist/stats');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create banlist entry
   * @param {object} data - Banlist data
   */
  async createBanlistEntry(data) {
    try {
      const response = await apiClient.post('/api/admin/dns/banlist/create', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update banlist entry
   * @param {object} data - Banlist data with id
   */
  async updateBanlistEntry(data) {
    try {
      const response = await apiClient.post('/api/admin/dns/banlist/update', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete banlist entry
   * @param {number} id - Entry ID
   */
  async deleteBanlistEntry(id) {
    try {
      const response = await apiClient.delete('/api/admin/dns/banlist/delete', { data: { id } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Batch delete banlist entries
   * @param {array} ids - Entry IDs
   */
  async batchDeleteBanlist(ids) {
    try {
      const response = await apiClient.post('/api/admin/dns/banlist/batch-delete', { ids });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Cleanup expired banlist entries
   */
  async cleanupBanlist() {
    try {
      const response = await apiClient.post('/api/admin/dns/banlist/cleanup');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // DNSSEC Management
  // ==========================================

  /**
   * Get DNSSEC status for a zone
   * @param {number} zoneId - Zone ID
   */
  async getDnssecStatus(zoneId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/dnssec/status?zone_id=${zoneId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get all zones with DNSSEC enabled
   */
  async getDnssecZones() {
    try {
      const response = await apiClient.get('/api/admin/dns/dnssec/zones');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNSSEC keys for a zone
   * @param {number} zoneId - Zone ID
   */
  async getDnssecKeys(zoneId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/dnssec/keys?zone_id=${zoneId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Enable DNSSEC for a zone
   * @param {number} zoneId - Zone ID
   * @param {string} algorithm - DNSSEC algorithm (default: ECDSAP256SHA256)
   */
  async enableDnssec(zoneId, algorithm = 'ECDSAP256SHA256') {
    try {
      const response = await apiClient.post('/api/admin/dns/dnssec/enable', {
        zone_id: zoneId,
        algorithm
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Disable DNSSEC for a zone
   * @param {number} zoneId - Zone ID
   */
  async disableDnssec(zoneId) {
    try {
      const response = await apiClient.post('/api/admin/dns/dnssec/disable', {
        zone_id: zoneId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Regenerate DNSSEC keys for a zone
   * @param {number} zoneId - Zone ID
   * @param {string} algorithm - DNSSEC algorithm (default: ECDSAP256SHA256)
   */
  async regenerateDnssec(zoneId, algorithm = 'ECDSAP256SHA256') {
    try {
      const response = await apiClient.post('/api/admin/dns/dnssec/regenerate', {
        zone_id: zoneId,
        algorithm
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * DNSSEC safe key rollover: action = start_zsk|start_ksk|confirm_ds|cancel|auto_ds|dismiss
   */
  async dnssecRollover(zoneId, action) {
    try {
      const response = await apiClient.post('/api/admin/dns/dnssec/rollover', { zone_id: zoneId, action });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // DNS Proxy Config Management
  // ==========================================

  /**
   * Get proxy configuration for a node
   * @param {number} nodeId - Node ID
   */
  async getProxyConfig(nodeId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/proxy/config?node_id=${nodeId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update proxy configuration
   * @param {object} config - Proxy config with node_id
   */
  async updateProxyConfig(config) {
    try {
      const response = await apiClient.post('/api/admin/dns/proxy/config', config);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get upstream servers for a proxy node
   * @param {number} nodeId - Node ID
   */
  async getProxyUpstreams(nodeId) {
    try {
      const response = await apiClient.get(`/api/admin/dns/proxy/upstreams?node_id=${nodeId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Add an upstream server to a proxy node
   * @param {object} upstream - Upstream data {node_id, addr, weight, status}
   */
  async addProxyUpstream(upstream) {
    try {
      const response = await apiClient.post('/api/admin/dns/proxy/upstreams/add', upstream);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update an upstream server
   * @param {object} upstream - Upstream data {id, weight, status, addr}
   */
  async updateProxyUpstream(upstream) {
    try {
      const response = await apiClient.post('/api/admin/dns/proxy/upstreams/update', upstream);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete an upstream server
   * @param {number} id - Upstream ID
   */
  async deleteProxyUpstream(id) {
    try {
      const response = await apiClient.delete(`/api/admin/dns/proxy/upstreams/delete?id=${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Products
  // ==========================================

  /**
   * Get DNS products list
   */
  async getDnsProducts() {
    try {
      const response = await apiClient.get('/api/admin/dns/products');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Analytics
  // ==========================================

  /**
   * Get DNS analytics overview (both DNS server and proxy)
   * @param {object} params - Query parameters (start_time, end_time)
   */
  async getAnalyticsOverview(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/overview?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNS server stats
   * @param {object} params - Query parameters (start_time, end_time, zone)
   */
  async getAnalyticsStats(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/stats?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNS proxy stats
   * @param {object} params - Query parameters (start_time, end_time)
   */
  async getAnalyticsProxyStats(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/proxy-stats?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNS proxy top blocked qnames
   * @param {object} params - Query parameters (start_time, end_time)
   */
  async getProxyBlocked(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/proxy-blocked?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNS proxy top query names
   * @param {object} params - Query parameters (start_time, end_time, limit)
   */
  async getProxyTopQnames(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/proxy-top-qnames?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNS proxy query type distribution
   * @param {object} params - Query parameters (start_time, end_time)
   */
  async getProxyQtypeDistribution(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/proxy-qtype-distribution?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get DNS proxy query trend
   * @param {object} params - Query parameters (start_time, end_time, interval)
   */
  async getProxyQueryTrend(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/proxy-query-trend?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get zone-specific analytics
   * @param {number} zoneId - Zone ID
   * @param {object} params - Query parameters (start_time, end_time)
   */
  async getZoneAnalytics(zoneId, params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/zone/${zoneId}?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get top zones by query count
   * @param {object} params - Query parameters (start_time, end_time, limit)
   */
  async getTopZones(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/zones?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get top query names
   * @param {object} params - Query parameters (start_time, end_time, limit, zone)
   */
  async getTopQnames(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/top-qnames?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get query type distribution
   * @param {object} params - Query parameters (start_time, end_time, zone)
   */
  async getQtypeDistribution(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/qtype-distribution?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get geo distribution
   * @param {object} params - Query parameters (start_time, end_time, zone)
   */
  async getGeoDistribution(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/geo-distribution?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get query trend
   * @param {object} params - Query parameters (start_time, end_time, interval, zone)
   */
  async getQueryTrend(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dns/analytics/query-trend?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new DNSService();
